import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  useListCustomRiskModels,
  useGetCustomRiskModel,
  useCreateCustomRiskModel,
  useUpdateCustomRiskModel,
  useDeleteCustomRiskModel,
  useListDiligentRiskTemplates,
  useListTemplateQuestions,
  useGetSponsorListForFilters,
  useGetRiskMethodology,
  useSyncRubricFromAirtable,
} from "../../custom-hooks/queries";
import { computeCriticality, resolveConfig } from "../RFIS/readonly/riskAggregates";
import NotificationModal from "../../components/NotificationModal";
import {
  ArrowPathIcon,
  PlusIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/24/outline";

/**
 * 14032 — Admin Custom Risk Model authoring page.
 *
 * Route: /Backend/RiskModels
 *
 * Layout:
 *   [Template dropdown]  [Sponsor dropdown]            ← scope picker
 *   ┌──────────────────────┬──────────────────────────────────────────┐
 *   │ Models in scope      │  Editor                                  │
 *   │  + New model         │   Name / Description                     │
 *   │  • Acme-High-Risk    │   Question table (one row per question   │
 *   │  • …                 │   in the template — Code · Text · Base   │
 *   │                      │   I/L/D · Override I/L/D · Risk · Tier) │
 *   │                      │   Save / Cancel / Delete                 │
 *   └──────────────────────┴──────────────────────────────────────────┘
 *
 * Admin picks template + sponsor from real dropdowns (no Guid pasting).
 * Editor lists every question in the template; admin sets I/L/D overrides
 * with 1-5 dropdowns on whichever subset they need. The save payload only
 * includes rows with at least one non-null override.
 */
const AdminRiskModels = () => {
  const [templateId, setTemplateId] = useState("");
  const [sponsorId, setSponsorId] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [creating, setCreating] = useState(false);

  const validScope = !!templateId && !!sponsorId;

  const { data: models, isLoading: listLoading, refetch: refetchList } = useListCustomRiskModels(
    {
      templateId: validScope ? templateId : undefined,
      sponsorId: validScope ? sponsorId : undefined,
      includeInactive: true,
    },
    { enabled: validScope }
  );

  // Switching scope drops any in-progress selection.
  useEffect(() => {
    setEditingId(null);
    setCreating(false);
  }, [templateId, sponsorId]);

  const [notification, setNotification] = useState({ open: false, title: "", desc: "", error: false });
  const syncMutation = useSyncRubricFromAirtable();

  const handleSync = async () => {
    const result = await syncMutation.mutateAsync();
    if (result?.ok) {
      const r = result.data || {};
      setNotification({
        open: true,
        error: false,
        title: "Airtable sync complete",
        desc: `Synced ${r.QuestionsUpserted ?? 0} questions across ${r.TemplatesUpserted ?? 0} template${
          (r.TemplatesUpserted ?? 0) === 1 ? "" : "s"
        } and ${r.SectionsUpserted ?? 0} section${(r.SectionsUpserted ?? 0) === 1 ? "" : "s"}.${
          r.Warnings?.length ? ` ${r.Warnings.length} warning${r.Warnings.length === 1 ? "" : "s"}.` : ""
        }`,
      });
    } else {
      setNotification({
        open: true,
        error: true,
        title: "Airtable sync failed",
        desc: result?.message || "Unable to sync the Risk Rubric from Airtable. Check the BE logs.",
      });
    }
  };

  return (
    <div className="px-6 py-6 space-y-5 bg-gray-50 min-h-screen">
      <PageHeader
        onSync={handleSync}
        syncing={syncMutation.isLoading}
        lastSyncedAt={syncMutation.data?.data?.SourceName ? new Date().toISOString() : null}
      />

      {syncMutation.isLoading ? (
        <div className="rounded-lg border border-indigo-200 bg-indigo-50/70 px-4 py-3 flex items-center gap-3">
          <ArrowPathIcon className="h-5 w-5 text-indigo-600 animate-spin" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-indigo-900">
              Syncing Risk Rubric from Airtable…
            </p>
            <p className="text-xs text-indigo-700/80">
              Pulling templates, sections, questions, and I / L / D values. This can take
              up to a minute on a cold cache.
            </p>
            <div className="mt-2 h-1.5 w-full rounded-full bg-indigo-100 overflow-hidden">
              <div className="h-full w-1/3 bg-indigo-500 rounded-full animate-pulse" />
            </div>
          </div>
        </div>
      ) : null}

      <ScopePicker
        templateId={templateId}
        setTemplateId={setTemplateId}
        sponsorId={sponsorId}
        setSponsorId={setSponsorId}
      />

      {!validScope ? (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center">
          <ShieldCheckIcon className="mx-auto h-10 w-10 text-gray-300" aria-hidden="true" />
          <p className="mt-3 text-sm font-medium text-gray-700">Select a scope to begin</p>
          <p className="mt-1 text-xs text-gray-500">
            Pick a template and a sponsor above to view or create custom risk models.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-12 gap-4">
          <aside className="col-span-3">
            <div className="rounded-lg border border-gray-200 bg-white p-3 space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-gray-800">Models in scope</h2>
                <button
                  type="button"
                  onClick={() => {
                    setCreating(true);
                    setEditingId(null);
                  }}
                  className="inline-flex items-center gap-1 rounded-md bg-indigo-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-indigo-700 shadow-sm"
                >
                  <PlusIcon className="h-3.5 w-3.5" />
                  New model
                </button>
              </div>
              {listLoading ? (
                <div className="text-xs text-gray-500 px-1 py-2">Loading…</div>
              ) : (models?.length ?? 0) === 0 ? (
                <EmptyModelsHint />
              ) : (
                <ul className="space-y-1.5">
                  {models.map((m) => (
                    <li key={m.custom_risk_model_id}>
                      <ModelListItem
                        model={m}
                        isActive={editingId === m.custom_risk_model_id}
                        onSelect={() => {
                          setEditingId(m.custom_risk_model_id);
                          setCreating(false);
                        }}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </aside>

          <section className="col-span-9">
            {creating ? (
              <ModelEditor
                key="new"
                templateId={templateId}
                sponsorId={sponsorId}
                modelId={null}
                onSaved={(newId) => {
                  setCreating(false);
                  setEditingId(newId);
                  refetchList();
                  setNotification({ open: true, error: false, title: "Model saved", desc: "Your custom risk model is now active for this scope." });
                }}
                onCancel={() => setCreating(false)}
                onError={(msg) => setNotification({ open: true, error: true, title: "Couldn't save model", desc: msg })}
              />
            ) : editingId ? (
              <ModelEditor
                key={editingId}
                templateId={templateId}
                sponsorId={sponsorId}
                modelId={editingId}
                onSaved={() => {
                  refetchList();
                  setNotification({ open: true, error: false, title: "Changes saved", desc: "Your overrides have been updated." });
                }}
                onCancel={() => setEditingId(null)}
                onDeleted={() => {
                  setEditingId(null);
                  refetchList();
                  setNotification({ open: true, error: false, title: "Model deleted", desc: "The custom risk model has been removed." });
                }}
                onError={(msg) => setNotification({ open: true, error: true, title: "Couldn't save model", desc: msg })}
              />
            ) : (
              <BlankEditorState onCreate={() => setCreating(true)} />
            )}
          </section>
        </div>
      )}

      <NotificationModal
        open={notification.open}
        setOpen={(open) => setNotification((prev) => ({ ...prev, open }))}
        title={notification.title}
        desc={notification.desc}
        error={notification.error}
      />
    </div>
  );
};

const PageHeader = ({ onSync, syncing }) => (
  <header className="rounded-lg border border-gray-200 bg-white p-5 flex items-start justify-between gap-4">
    <div>
      <div className="flex items-center gap-2">
        <ShieldCheckIcon className="h-6 w-6 text-indigo-600" />
        <h1 className="text-xl font-semibold text-gray-900">Custom Risk Models</h1>
      </div>
      <p className="mt-1 text-sm text-gray-500 max-w-3xl">
        Diligent-curated variants of the Risk Model, scoped to a single (template, sponsor) pair.
        Overrides layer on top of the Airtable-synced Diligent Risk Model — questions you don&apos;t
        touch inherit base values automatically.
      </p>
      <Link
        to="/Backend/RiskModels/Diligent"
        className="mt-2 inline-flex items-center text-[13px] font-medium text-indigo-600 hover:text-indigo-700 hover:underline"
      >
        Edit the Diligent base model values for a template →
      </Link>
    </div>
    <div className="shrink-0 flex flex-col items-end gap-1">
      <button
        type="button"
        onClick={onSync}
        disabled={syncing}
        className="inline-flex items-center gap-2 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-100 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ArrowPathIcon className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
        {syncing ? "Syncing…" : "Sync from Airtable"}
      </button>
      <span className="text-[11px] text-gray-400">
        Pulls latest rubric (templates, sections, questions, I/L/D) from Airtable.
      </span>
    </div>
  </header>
);

const EmptyModelsHint = () => (
  <div className="rounded border border-dashed border-gray-200 p-3 text-center">
    <p className="text-xs text-gray-500">No custom models yet for this scope.</p>
    <p className="mt-1 text-[11px] text-gray-400">
      Click <span className="font-semibold text-indigo-600">New model</span> to author one.
    </p>
  </div>
);

const ModelListItem = ({ model, isActive, onSelect }) => {
  const overrideCount =
    model.override_count ?? model.overrides_count ?? model.overrides?.length ?? null;
  const createdAt = model.created_at || model.created_on || model.createdAt;
  const createdLabel = createdAt
    ? new Date(createdAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
    : null;
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full text-left rounded-lg border px-3 py-2.5 transition ${
        isActive
          ? "border-indigo-300 bg-indigo-50 ring-1 ring-indigo-200 shadow-sm"
          : "border-gray-200 hover:bg-gray-50 hover:border-gray-300"
      } ${model.is_active ? "" : "opacity-60"}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className={`truncate text-sm font-semibold ${isActive ? "text-indigo-700" : "text-gray-800"}`}>
            {model.name}
          </div>
          <div className="mt-0.5 text-[11px] text-gray-500 truncate">
            {model.author_email || "Unknown author"}
          </div>
        </div>
        <span
          className={`shrink-0 inline-flex items-center rounded-full border px-1.5 py-px text-[10px] font-semibold ${
            model.is_active
              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
              : "bg-gray-100 text-gray-500 border-gray-200"
          }`}
        >
          {model.is_active ? "Active" : "Inactive"}
        </span>
      </div>
      {(overrideCount != null || createdLabel) && (
        <div className="mt-1.5 flex items-center gap-2 text-[10px] text-gray-500">
          {overrideCount != null && (
            <span>
              <span className="font-semibold text-gray-700 tabular-nums">{overrideCount}</span>{" "}
              override{overrideCount === 1 ? "" : "s"}
            </span>
          )}
          {overrideCount != null && createdLabel && <span className="text-gray-300">·</span>}
          {createdLabel && <span>Created {createdLabel}</span>}
        </div>
      )}
    </button>
  );
};

const BlankEditorState = ({ onCreate }) => (
  <div className="rounded-lg border border-gray-200 bg-white p-10 text-center">
    <ShieldCheckIcon className="mx-auto h-10 w-10 text-gray-300" />
    <p className="mt-3 text-sm font-medium text-gray-700">No model selected</p>
    <p className="mt-1 text-xs text-gray-500">
      Pick a model on the left, or create a new one for this (template, sponsor) scope.
    </p>
    <button
      type="button"
      onClick={onCreate}
      className="mt-4 inline-flex items-center gap-1 rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700"
    >
      <PlusIcon className="h-3.5 w-3.5" />
      Create new model
    </button>
  </div>
);

const ScopePicker = ({ templateId, setTemplateId, sponsorId, setSponsorId }) => {
  const { data: templates, isLoading: templatesLoading } = useListDiligentRiskTemplates();
  const { data: sponsors, isLoading: sponsorsLoading } = useGetSponsorListForFilters();

  // Sponsor list shape varies (some endpoints return { sponsor_id, sponsor_name },
  // others return raw rows). Normalize to { id, name } so the dropdown isn't picky.
  const sponsorOptions = useMemo(() => {
    if (!Array.isArray(sponsors)) return [];
    return sponsors
      .map((s) => ({
        id: s?.sponsor_id || s?.id || s?.sponsorId,
        name: s?.sponsor_name || s?.name || s?.sponsorName || s?.organizationName,
      }))
      .filter((s) => s.id && s.name)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [sponsors]);

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-3">
        Scope
      </div>
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex-1 min-w-[18rem]">
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Template
          </label>
          <select
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
            disabled={templatesLoading}
            className="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="">
              {templatesLoading ? "Loading templates…" : "— pick a template —"}
            </option>
            {(templates ?? []).map((t) => (
              <option key={t.diligent_risk_template_id} value={t.diligent_risk_template_id}>
                {t.template_name}
                {t.template_family ? ` (${t.template_family})` : ""}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1 min-w-[18rem]">
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Sponsor
          </label>
          <select
            value={sponsorId}
            onChange={(e) => setSponsorId(e.target.value)}
            disabled={sponsorsLoading}
            className="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="">
              {sponsorsLoading ? "Loading sponsors…" : "— pick a sponsor —"}
            </option>
            {sponsorOptions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

/**
 * 14032 — Single-model editor. Loads the template's full question list +
 * the current model's overrides (when editing). Merges them so every
 * question shows up as a row, with override controls pre-populated from
 * existing rows.
 */
const ModelEditor = ({ templateId, sponsorId, modelId, onSaved, onCancel, onDeleted, onError }) => {
  const { data: existing } = useGetCustomRiskModel(modelId);
  const { data: methodology } = useGetRiskMethodology({ enabled: true });
  const { data: questions, isLoading: questionsLoading } = useListTemplateQuestions(templateId, {
    enabled: !!templateId,
  });
  const config = useMemo(() => resolveConfig(methodology), [methodology]);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  /** @type {Record<string, {i:number|null, l:number|null, d:number|null}>} */
  const [overridesById, setOverridesById] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const [search, setSearch] = useState("");
  const [onlyOverridden, setOnlyOverridden] = useState(false);

  useEffect(() => {
    if (existing) {
      setName(existing.name ?? "");
      setDescription(existing.description ?? "");
      const next = {};
      for (const o of existing.overrides ?? []) {
        next[o.diligent_risk_question_id] = {
          i: o.impact_override ?? null,
          l: o.likelihood_override ?? null,
          d: o.detectability_override ?? null,
        };
      }
      setOverridesById(next);
    } else {
      setName("");
      setDescription("");
      setOverridesById({});
    }
  }, [existing, modelId]);

  const createMutation = useCreateCustomRiskModel();
  const updateMutation = useUpdateCustomRiskModel();
  const deleteMutation = useDeleteCustomRiskModel();
  const saving = createMutation.isLoading || updateMutation.isLoading;

  const overrideCount = useMemo(
    () =>
      Object.values(overridesById).filter(
        (v) => v && (v.i != null || v.l != null || v.d != null)
      ).length,
    [overridesById]
  );

  const handleSave = async () => {
    setErrorMessage("");
    const overrides = Object.entries(overridesById)
      .filter(([, v]) => v && (v.i != null || v.l != null || v.d != null))
      .map(([id, v]) => ({
        diligent_risk_question_id: id,
        impact_override: v.i,
        likelihood_override: v.l,
        detectability_override: v.d,
      }));
    const payload = {
      name: name.trim(),
      description: description?.trim() || null,
      diligent_risk_template_id: templateId,
      sponsor_id: sponsorId,
      overrides,
    };
    try {
      const result = modelId
        ? await updateMutation.mutateAsync({ customRiskModelId: modelId, payload })
        : await createMutation.mutateAsync(payload);
      if (!result?.ok) {
        const msg = result?.message || "Save failed. A model with the same name may already exist for this scope.";
        setErrorMessage(msg);
        onError?.(msg);
        return;
      }
      onSaved?.(result.data?.custom_risk_model_id ?? modelId);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        "Couldn't save. Check the BE logs.";
      setErrorMessage(msg);
      onError?.(msg);
    }
  };

  const handleDelete = async () => {
    if (!modelId) return;
    if (
      !window.confirm(
        `Delete custom risk model "${name}"? Historical scoring runs that used it will keep referencing the (now-inactive) row.`
      )
    )
      return;
    await deleteMutation.mutateAsync(modelId);
    onDeleted?.();
  };

  const updateOverride = (id, field, value) => {
    setOverridesById((prev) => ({
      ...prev,
      [id]: {
        i: field === "i" ? value : prev[id]?.i ?? null,
        l: field === "l" ? value : prev[id]?.l ?? null,
        d: field === "d" ? value : prev[id]?.d ?? null,
      },
    }));
  };

  const clearOverride = (id) =>
    setOverridesById((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });

  // Natural sort so codes order as humans expect (BA1.1 → BA1.2 → BA1.10).
  // Prefer the rubric-provided question_position when available; fall back
  // to a natural compare on question_code so admins never see "1, 10, 2, 3".
  const sortedQuestions = useMemo(() => {
    const arr = [...(questions ?? [])];
    arr.sort((a, b) => {
      if (a.question_position != null && b.question_position != null) {
        return a.question_position - b.question_position;
      }
      return naturalCompare(a.question_code, b.question_code);
    });
    return arr;
  }, [questions]);

  const filteredQuestions = useMemo(() => {
    const term = search.trim().toLowerCase();
    return sortedQuestions.filter((q) => {
      const ov = overridesById[q.diligent_risk_question_id];
      const hasOverride = ov && (ov.i != null || ov.l != null || ov.d != null);
      if (onlyOverridden && !hasOverride) return false;
      if (!term) return true;
      const hay = `${q.question_code} ${q.question_text_snapshot ?? ""} ${q.section_name ?? ""}`.toLowerCase();
      return hay.includes(term);
    });
  }, [sortedQuestions, overridesById, search, onlyOverridden]);

  // Group the filtered questions by section in the original sorted order so
  // sections render top-to-bottom matching the template's natural sequence
  // rather than alphabetical-by-section.
  const groupedSections = useMemo(() => {
    const order = [];
    const buckets = new Map();
    for (const q of filteredQuestions) {
      const key = q.section_name || "Uncategorized";
      if (!buckets.has(key)) {
        buckets.set(key, { name: key, questions: [], overrideCount: 0 });
        order.push(key);
      }
      const bucket = buckets.get(key);
      bucket.questions.push(q);
      const ov = overridesById[q.diligent_risk_question_id];
      if (ov && (ov.i != null || ov.l != null || ov.d != null)) {
        bucket.overrideCount += 1;
      }
    }
    return order.map((k) => buckets.get(k));
  }, [filteredQuestions, overridesById]);

  const [collapsedSections, setCollapsedSections] = useState(() => new Set());
  const toggleSection = (name) =>
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });

  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
      {/* Top action bar — title, status, and primary actions live here so they
          stay aligned with the form fields below and never float in the corner. */}
      <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-gray-200 bg-gradient-to-b from-gray-50 to-white">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-9 w-9 rounded-md bg-indigo-100 flex items-center justify-center shrink-0">
            <ShieldCheckIcon className="h-5 w-5 text-indigo-600" />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-gray-900 truncate">
              {modelId ? name || "Edit risk model" : "New custom risk model"}
            </h3>
            <p className="text-[11px] text-gray-500">
              {overrideCount > 0
                ? `${overrideCount} override${overrideCount === 1 ? "" : "s"} pending save`
                : "No overrides yet — every question inherits stock rubric values"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {modelId ? (
            <button
              type="button"
              onClick={handleDelete}
              className="inline-flex items-center gap-1 rounded-md border border-rose-200 bg-white px-3 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-50"
            >
              <TrashIcon className="h-3.5 w-3.5" />
              Delete
            </button>
          ) : null}
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !name.trim()}
            className="inline-flex items-center justify-center gap-1 rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-50 shadow-sm"
          >
            {saving ? (
              <>
                <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
                Saving…
              </>
            ) : (
              <>
                <CheckCircleIcon className="h-3.5 w-3.5" />
                {modelId ? "Save changes" : "Create model"}
              </>
            )}
          </button>
        </div>
      </div>

      <div className="p-5 space-y-4">
        {/* Identity — name + description, no longer competing with action buttons. */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-1">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Model name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Acme High-Risk RTSM"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-medium focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Description <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What's this model for? Who's using it?"
              rows={2}
              className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
            />
          </div>
        </div>

        {errorMessage ? (
          <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700 flex items-start gap-2">
            <ExclamationCircleIcon className="h-4 w-4 shrink-0 mt-px" />
            <span>{errorMessage}</span>
          </div>
        ) : null}

        {/* Filter row — search left, show-only-overridden middle, counter right.
            Wraps cleanly on narrow screens; gap-3 keeps controls breathing. */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 pt-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search question code or text…"
                className="rounded-md border border-gray-300 pl-8 pr-3 py-1.5 text-sm w-72 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>
            <label className="inline-flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={onlyOverridden}
                onChange={(e) => setOnlyOverridden(e.target.checked)}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span>Show only overridden</span>
              <span className="inline-flex items-center justify-center min-w-[1.25rem] h-4 px-1 rounded-full bg-indigo-100 text-indigo-700 text-[10px] font-semibold tabular-nums">
                {overrideCount}
              </span>
            </label>
          </div>
          <div className="flex items-center gap-3 text-xs text-gray-500">
            <span className="tabular-nums">
              {filteredQuestions.length} / {questions?.length ?? 0} questions
            </span>
          </div>
        </div>

        {/* Question table — grouped by section, collapsible per section.
            Each row that has an override gets a 2pt indigo left border so
            modified rows are scannable at a glance. */}
        <div className="overflow-x-auto rounded-md border border-gray-200 max-h-[60vh]">
          <table className="min-w-full text-xs">
            <thead className="bg-gray-50 text-left text-gray-600 sticky top-0 z-10">
              <tr>
                <th className="px-3 py-2 w-24 font-medium">Code</th>
                <th className="px-3 py-2 font-medium">Question</th>
                <th className="px-3 py-2 w-20 text-center font-medium">Base I/L/D</th>
                <th className="px-3 py-2 w-24 font-medium">Impact</th>
                <th className="px-3 py-2 w-24 font-medium">Likelihood</th>
                <th className="px-3 py-2 w-24 font-medium">Detectability</th>
                <th className="px-3 py-2 w-32 font-medium">Effective</th>
                <th className="px-3 py-2 w-12"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {questionsLoading ? (
                <tr>
                  <td colSpan={8} className="px-3 py-4 text-center text-gray-500">
                    Loading questions…
                  </td>
                </tr>
              ) : filteredQuestions.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-3 py-6 text-center text-gray-500">
                    {onlyOverridden && overrideCount === 0
                      ? "No overrides yet. Step any I / L / D below to start customizing."
                      : "No questions match the current filters."}
                  </td>
                </tr>
              ) : (
                groupedSections.map((section) => {
                  const collapsed = collapsedSections.has(section.name);
                  return (
                    <React.Fragment key={section.name}>
                      <tr className="bg-gray-50/80 border-t border-gray-200">
                        <td colSpan={8} className="px-3 py-1.5">
                          <button
                            type="button"
                            onClick={() => toggleSection(section.name)}
                            className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-gray-600 hover:text-gray-900"
                          >
                            <span className={`inline-block w-3 transition-transform ${collapsed ? "" : "rotate-90"}`}>
                              ▸
                            </span>
                            <span className="normal-case tracking-normal text-xs">{section.name}</span>
                            <span className="rounded-full bg-gray-200/70 px-1.5 py-px text-[10px] font-medium text-gray-700 tabular-nums">
                              {section.questions.length}
                            </span>
                            {section.overrideCount > 0 && (
                              <span className="rounded-full bg-indigo-100 px-1.5 py-px text-[10px] font-semibold text-indigo-700 tabular-nums">
                                {section.overrideCount} overridden
                              </span>
                            )}
                          </button>
                        </td>
                      </tr>
                      {!collapsed &&
                        section.questions.map((q) => {
                          const ov = overridesById[q.diligent_risk_question_id];
                          const hasOverride =
                            ov && (ov.i != null || ov.l != null || ov.d != null);
                          const effI = ov?.i ?? q.impact ?? null;
                          const effL = ov?.l ?? q.likelihood ?? null;
                          const effD = ov?.d ?? q.detectability ?? null;
                          const rs =
                            effI != null && effL != null && effD != null
                              ? effI * effL * effD
                              : null;
                          const crit =
                            rs != null ? computeCriticality(rs, effI, config) : null;
                          return (
                            <tr
                              key={q.diligent_risk_question_id}
                              className={`group ${
                                hasOverride
                                  ? "bg-indigo-50/30 border-l-2 border-l-indigo-400"
                                  : "border-l-2 border-l-transparent"
                              }`}
                            >
                              <td className="px-3 py-2 font-mono text-[11px] text-gray-700 align-top">
                                {q.question_code}
                              </td>
                              <td className="px-3 py-2 align-top">
                                <div
                                  className="text-gray-800 truncate max-w-[34rem]"
                                  title={q.question_text_snapshot ?? ""}
                                >
                                  {q.question_text_snapshot ?? (
                                    <em className="text-gray-400">No text</em>
                                  )}
                                </div>
                              </td>
                              <td className="px-3 py-2 text-center text-[11px] tabular-nums text-gray-500 align-top">
                                {q.impact ?? "?"} / {q.likelihood ?? "?"} /{" "}
                                {q.detectability ?? "?"}
                              </td>
                              <td className="px-3 py-2 align-top">
                                <ScoreStepper
                                  value={ov?.i ?? null}
                                  base={q.impact ?? null}
                                  onChange={(v) =>
                                    updateOverride(q.diligent_risk_question_id, "i", v)
                                  }
                                />
                              </td>
                              <td className="px-3 py-2 align-top">
                                <ScoreStepper
                                  value={ov?.l ?? null}
                                  base={q.likelihood ?? null}
                                  onChange={(v) =>
                                    updateOverride(q.diligent_risk_question_id, "l", v)
                                  }
                                />
                              </td>
                              <td className="px-3 py-2 align-top">
                                <ScoreStepper
                                  value={ov?.d ?? null}
                                  base={q.detectability ?? null}
                                  onChange={(v) =>
                                    updateOverride(q.diligent_risk_question_id, "d", v)
                                  }
                                />
                              </td>
                              <td className="px-3 py-2 align-top">
                                <TierBadge score={rs} tier={crit} />
                              </td>
                              <td className="px-3 py-2 text-right align-top">
                                {hasOverride ? (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      clearOverride(q.diligent_risk_question_id)
                                    }
                                    className="text-rose-600 hover:text-rose-800 text-[11px] opacity-0 group-hover:opacity-100 transition"
                                    title="Clear all overrides on this question"
                                  >
                                    Clear
                                  </button>
                                ) : null}
                              </td>
                            </tr>
                          );
                        })}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

/**
 * Compact 1–5 stepper. The override value renders in violet; clicking +/−
 * walks the value within [1,5]. When a step would land back on the base
 * rubric value, the override is cleared (returns to inherited). Empty value
 * starts from the base so single-click adjustments feel intuitive.
 */
const ScoreStepper = ({ value, base, onChange }) => {
  const step = (delta) => {
    const start = value ?? base ?? 3;
    const next = Math.max(1, Math.min(5, start + delta));
    onChange(next === base ? null : next);
  };
  const hasOverride = value != null;
  return (
    <div
      className={`inline-flex items-center rounded-md border bg-white overflow-hidden ${
        hasOverride ? "border-indigo-300 ring-1 ring-indigo-200/60" : "border-gray-200"
      }`}
    >
      <button
        type="button"
        onClick={() => step(-1)}
        className="w-6 h-7 flex items-center justify-center text-gray-400 hover:text-gray-800 hover:bg-gray-50 transition"
        title="Decrease"
      >
        −
      </button>
      <span
        className={`w-7 text-center text-[12px] tabular-nums font-semibold border-x border-gray-100 leading-7 ${
          hasOverride ? "text-indigo-700" : "text-gray-400"
        }`}
      >
        {value ?? base ?? "—"}
      </span>
      <button
        type="button"
        onClick={() => step(1)}
        className="w-6 h-7 flex items-center justify-center text-gray-400 hover:text-gray-800 hover:bg-gray-50 transition"
        title="Increase"
      >
        +
      </button>
    </div>
  );
};

const TIER_STYLES = {
  Critical: "bg-rose-50 text-rose-700 border-rose-200",
  High: "bg-orange-50 text-orange-700 border-orange-200",
  Medium: "bg-amber-50 text-amber-700 border-amber-200",
  Low: "bg-blue-50 text-blue-700 border-blue-200",
  Compliant: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

/** Risk score + tier in a single color-coded chip so distribution is scannable at a glance. */
const TierBadge = ({ score, tier }) => {
  if (score == null) {
    return <span className="text-gray-400">—</span>;
  }
  const style = TIER_STYLES[tier] || "bg-gray-50 text-gray-600 border-gray-200";
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-[11px] font-medium ${style}`}
    >
      <span className="font-bold tabular-nums">{score}</span>
      <span className="opacity-60">·</span>
      <span>{tier ?? "—"}</span>
    </span>
  );
};

/**
 * Natural sort so question codes order as humans expect:
 * BA1.1 → BA1.2 → BA1.10 (not BA1.1 → BA1.10 → BA1.2).
 */
const naturalCompare = (a, b) =>
  String(a ?? "").localeCompare(String(b ?? ""), undefined, {
    numeric: true,
    sensitivity: "base",
  });

export default AdminRiskModels;
